/**
 * AI Analysis Service
 *
 * Reads implementation guides and — for the editable Implementation Guide
 * Framework — writes user edits back. Two write paths, both RLS-gated (the
 * ai_insert / ai_update policies scope writes to the task owner or an accepted
 * participant): starting a fresh (free) guide, and saving edits to any guide.
 *
 * The AI *pre-fill* (paid $1) is still generated server-side only, by the
 * 3rd-eye-feel Worker — see workers/3rdeyefeel-api/src/routes/plans.ts. GNK
 * never calls an LLM; it only persists what the user types.
 */

import { AIAnalysis, GuideComponent } from '../../types';
import { supabase, TABLES } from '../../api/db';
import { normalizeAIAnalysisRow } from '../../utils/taskRecords';
import { emptyGuideComponents } from '../../constants/guideFramework';

export class AIAnalysisService {
  async getAnalysis(taskId: string): Promise<AIAnalysis | null> {
    const { data, error } = await supabase
      .from(TABLES.AI_ANALYSES)
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    return normalizeAIAnalysisRow(data as Record<string, unknown>);
  }

  /** Create an empty, self-authored guide scaffold for a task that has none.
   * Used by the free path so the owner can start filling the framework in. */
  async createEmptyGuide(taskId: string): Promise<AIAnalysis | null> {
    const { data, error } = await supabase
      .from(TABLES.AI_ANALYSES)
      .upsert(
        {
          task_id: taskId,
          status: 'completed',
          source: 'self-authored',
          ai_provider: 'anthropic',
          summary: '',
          guide_components: emptyGuideComponents(),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'task_id' }
      )
      .select('*')
      .maybeSingle();
    if (error || !data) {
      if (error) console.error('createEmptyGuide failed:', error.message);
      return null;
    }
    return normalizeAIAnalysisRow(data as Record<string, unknown>);
  }

  /** Persist edits to a guide's components / summary. Preserves `source`
   * (paid guides stay '3rdeyefeel-paid' when edited). */
  async saveGuide(
    taskId: string,
    payload: { components: GuideComponent[]; summary?: string }
  ): Promise<boolean> {
    const patch: Record<string, unknown> = {
      guide_components: payload.components,
      status: 'completed',
      updated_at: new Date().toISOString(),
    };
    if (payload.summary !== undefined) patch.summary = payload.summary;
    const { error } = await supabase
      .from(TABLES.AI_ANALYSES)
      .update(patch)
      .eq('task_id', taskId);
    if (error) {
      console.error('saveGuide failed:', error.message);
      return false;
    }
    return true;
  }
}

// Singleton instance
let aiAnalysisServiceInstance: AIAnalysisService | null = null;

export function getAIAnalysisService(): AIAnalysisService {
  if (!aiAnalysisServiceInstance) {
    aiAnalysisServiceInstance = new AIAnalysisService();
  }
  return aiAnalysisServiceInstance;
}

